import axios from "axios";

import { SkillType } from "./model/Constants";
import {
  CandidateFilterParams,
  CandidateSummaryDto,
  LicenseFilterParams, LicenseRequestDto, ResponseDto,
  TestDto,
  TestIdResponseDto,
  UserDto
} from "./model/Dto";

// =============================================================================
// == Axios config =============================================================
// =============================================================================

export const axiosBase = axios.create({
  // baseURL: process.env.REACT_APP_BACKEND_URL,
  baseURL: window.location.origin+"/api",
});
axiosBase.defaults.withCredentials = true;

const isStatus2xx = (status: number) => status >= 200 && status < 300;

const isStatus2xxOr401 = (status: number) =>
  isStatus2xx(status) || status === 401;

const isStatus2xxOr404 = (status: number) =>
  isStatus2xx(status) || status === 404;

// =============================================================================
// == Delay ====================================================================
// =============================================================================

// Based on: https://dnlytras.com/snippets/adding-delay-in-dev/

export function sleep(ms = 400): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

axiosBase.interceptors.response.use(async (response) => {
  if (process.env.NODE_ENV === "development") {
    await sleep();
  }
  return response;
});

// =============================================================================
// == Authentication-related endpoints =========================================
// =============================================================================

export const getCurrentUser = () => {
  return axiosBase.get<UserDto>("/users/me", {
    validateStatus: isStatus2xxOr401,
  });
};

export const login = (username: string, password: string) => {
  const data = new FormData();
  data.append("username", username);
  data.append("password", password);
  return axiosBase.post<void>("/login", data, {
    headers: { "content-type": "application/x-www-form-urlencoded" },
  });
};

export const logout = () => {
  return axiosBase.post<void>("/perform_logout");
};


export const postUserAcceptsTerms = (accepts: boolean) => {
  const data = new FormData();
  data.append("acceptsS", ""+accepts);
  return axiosBase.post<void>(`/users/updateAcceptsTerms/`, data)
};

// =============================================================================
// == Admin endpoints ==========================================================
// =============================================================================

export const getTests = () => axiosBase.get<TestDto[]>("/admin/tests");

export const getCandidates = (params: CandidateFilterParams, isPublic?: boolean) => {
  const headersParam : any = {
    "startDate": params.startDateValue,
    "endDate": params.endDateValue,
    "username": params.username,
    "name": params.name,
    "includeInvited": params.includeInvited,
    "includeInProgress": params.includeInProgress,
    "includeCompleted": params.includeCompleted,
    "group": params.group,
    "mockCandidates": params.mockCandidates}  ;

  const urlType = params.isTeacher ? `teacher` :
        isPublic || params.institutionOnly ? `group` : `admin`;

  let urlSection = `summary`;
  if (urlType === `admin`) {
    headersParam.institution = params.institution;
    headersParam.region = params.region;
    headersParam.testName = params.testName;
  } else if (isPublic) {
    if (params.institutionOnly !== undefined && params.institutionOnly) {
      headersParam.institution = params.institution;
      headersParam.licensesOnly = true;

      //CHECK THIS, it's only valid for licenses

      headersParam.region = params.region;
      urlSection = `Isummary`;
    }
  }
  return axiosBase.get<CandidateSummaryDto[]>(`/${urlType}/candidates/${urlSection}`, {
    headers: headersParam, });
}

export const getUsers = (institution: string, region: string, group: string) => {
  return axiosBase.get<UserDto[]>("/admin/users/summary", {
    headers: {
      region: region,
      institution: institution,
      group: group
    }, });
}

export const postTests = (file: File) => {
  const data = new FormData();
  data.append("test", file);
  return axiosBase.post<void>("/admin/tests", data);
};

export const shutdown = () => {
  return axiosBase.post<void>("/adminX/shutdown");
};

export const getTest = (id: string | undefined) =>
  axiosBase.get<TestDto>(`/admin/tests/${id}`);

export const deleteTest = (id: string) =>
  axiosBase.delete<void>(`/admin/tests/${id}`);

export const deleteUser = (username: string) =>
  axiosBase.delete<void>(`/admin/users/${username}`);

export const updateUser = (username: string,
                           usernameValue: string,
                           nameValue: string,
                           institutionValue: string,
                           regionValue: string,
                           emailValue: string,
                           uGroupValue: string,
                           passwordValue: string,
                           roleValue: string,
                           statusValue: string) => {
  const data = new FormData();
  data.append("usernameValue", usernameValue);
  data.append("nameValue", nameValue);
  data.append("institutionValue", institutionValue);
  data.append("regionValue", regionValue);
  data.append("emailValue", emailValue);
  data.append("uGroupValue", uGroupValue);
  data.append("passwordValue", passwordValue);
  data.append("roleValue", roleValue);
  data.append("statusValue", statusValue);
  return axiosBase.post<void>(`/admin/users/update/${username}`, data)
};

export const postUsers = (file: File) => {
  const data = new FormData();
  data.append("users", file);
  return axiosBase.post<void>("/admin/users", data);
};

// unused
export const uploadCertificatesExcel = (file: File) => {
  const data = new FormData();
  data.append("data", file);
  return axiosBase.post<Blob>("/admin/users/certificates", data);
};
export const downloadCertificatesExcel = (file: File) => {
  const data = new FormData();
  data.append("data", file);
  return axiosBase.post<Blob>("/admin/users/getCertificates", data,
    {
      responseType: 'blob'});
};

export const downloadResultCertificatesExcel = (file: File) => {
  const data = new FormData();
  data.append("data", file);
  return axiosBase.post<Blob>("/admin/candidates/getResultsCertificates", data,
    {
      responseType: 'blob'});
};

export const downloadCertificatesFiltered = (params?: CandidateFilterParams, isPublic?: boolean) => {

  const headersParam : any = {};
  let urlSection = `certificates`;
  let urlType = `teacher`;

  if (params!== undefined) {
    headersParam.startDate= params.startDateValue;
    headersParam.endDate= params.endDateValue;
    headersParam.username= params.username;
    headersParam.name= params.name;
    headersParam.includeInvited= params.includeInvited;
    headersParam.includeInProgress= params.includeInProgress;
    headersParam.includeCompleted= params.includeCompleted;
    headersParam.group= params.group;
    headersParam.mockCandidates= params.mockCandidates ;

      urlType = params.isTeacher ? `teacher` : `admin`;

      if (urlType === `admin`) {
        headersParam.institution = params.institution;
        headersParam.region = params.region;
        headersParam.testName = params.testName;
      }
  }
  return axiosBase.get<Blob>(`/${urlType}/candidates/${urlSection}`, {
    headers: headersParam,
      responseType: 'blob'});
};










export const postCandidates = (file: File) => {
  const data = new FormData();
  data.append("candidates", file);
  return axiosBase.post<Blob>("/admin/candidates", data,
    {
      responseType: 'blob'});
};

export const postAnswersExcel = (file: File) => {
  const data = new FormData();
  data.append("answers", file);
  return axiosBase.post<void>("/admin/candidates/answersExcel", data);
};

export const requestAnswersExcel = (params: CandidateFilterParams) => {
  const urlType = params.isTeacher ? `teacher`: `admin`;
  const headersParam : any = {
    "startDate": params.startDateValue,
    "endDate": params.endDateValue,
    "username": params.username,
    "name": params.name,
    "group": params.group,
    "includeInvited": params.includeInvited,
    "includeInProgress": params.includeInProgress,
    "includeCompleted": params.includeCompleted}  ;
  if (!params.isTeacher) {
    headersParam.institution = params.institution;
    headersParam.region = params.region;
    headersParam.testName = params.testName;
  }
  return axiosBase.get<Blob>(`/${urlType}/candidates/answersExcel`, {
    validateStatus: isStatus2xxOr404,
    responseType: 'blob',
    headers: headersParam, });
};

export const requestAnswersSpeechace = (params: CandidateFilterParams) => {
  const urlType = params.isTeacher ? `teacher`: `admin`;
  const headersParam : any = {
    "startDate": params.startDateValue,
    "endDate": params.endDateValue,
    "username": params.username,
    "name": params.name,
    "group": params.group,
    "includeInvited": params.includeInvited,
    "includeInProgress": params.includeInProgress,
    "includeCompleted": params.includeCompleted}  ;
  if (!params.isTeacher) {
    headersParam.institution = params.institution;
    headersParam.region = params.region;
    headersParam.testName = params.testName;
  }
  return axiosBase.get<Blob>(`/${urlType}/candidates/answersSpeechace`, {
    validateStatus: isStatus2xxOr404,
    responseType: 'blob',
    headers: headersParam, });
};

export const requestResults = (params: CandidateFilterParams) => {
  const urlType = params.isTeacher ? `teacher`: `admin`;
  const headersParam : any = {
    "startDate": params.startDateValue,
    "endDate": params.endDateValue,
    "username": params.username,
    "name": params.name,
    "group": params.group,
    "includeInvited": params.includeInvited,
    "includeInProgress": params.includeInProgress,
    "includeCompleted": params.includeCompleted}  ;
  //if (!params.isTeacher) {  // is admin
    headersParam.institution = params.institution;
    headersParam.region = params.region;
 // }

  return axiosBase.get<Blob>(`/${urlType}/candidates/results`, {
    validateStatus: isStatus2xxOr404,
    responseType: 'blob',
    headers: headersParam,
  });
};

export const requestDataJson = (params: CandidateFilterParams) => {
  const urlType = params.isTeacher ? `teacher`: `admin`;
  const headersParam : any = {"startDate": params.startDateValue,
    "endDate": params.endDateValue,
    "includeInvited": params.includeInvited,
    "includeInProgress": params.includeInProgress,
    "includeCompleted": params.includeCompleted}  ;
  if (!params.isTeacher) {
    headersParam.institution = params.institution;
    headersParam.region = params.region;
  }
  return axiosBase.get<JSON>(`/${urlType}/candidates/dataJson`, {
    validateStatus: isStatus2xxOr404,
    headers: headersParam,
  });
};

export const postDataJson = (fileString: String) => {
  const data = new FormData();
  data.append("data", fileString.toString());
  return axiosBase.post<void>("/admin/candidates/dataJson", data);
};

export const requestExportAnswers = (params: CandidateFilterParams) => {
  const urlType = params.isTeacher ? `teacher`: `admin`;
  const headersParam : any = {"startDate": params.startDateValue,
    "endDate": params.endDateValue,
    "includeInvited": params.includeInvited,
    "includeInProgress": params.includeInProgress,
    "includeCompleted": params.includeCompleted}  ;
  if (!params.isTeacher) {
    headersParam.institution = params.institution;
    headersParam.region = params.region;
  }
  return axiosBase.get<Blob>(`/${urlType}/candidates/exportAnswers`, {
    validateStatus: isStatus2xxOr404,
    responseType: 'blob',
    headers: headersParam,
  });
};

export const updateCandidate = (username:string, originalTest:string, token:string, skillType:string,
                                newTest:string, level:string, status:string, active:boolean,
                                enabledFrom:string, enabledTo:string, startTime:string, endTime:string,
                                multipleAttempts:number, questionLimit:number, timeLimit:number, timeLeft:number,
                                conditions:string, report:string, overall:number) => {
  const data = new FormData();
  data.append("newTest", newTest === null ? "" : newTest);
  data.append("level", level === null ? "" : level);
  data.append("status", status);
  data.append("active", ""+active);
  data.append("enabledFrom", enabledFrom === null ? "" : enabledFrom);
  data.append("enabledTo", enabledTo === null ? "" : enabledTo);
  data.append("startTime", startTime === null ? "" : startTime);
  data.append("endTime", endTime === null ? "" : endTime);
  data.append("multipleAttempts", multipleAttempts === null ? "" : ""+multipleAttempts);
  data.append("questionLimit", questionLimit === null ? "" : ""+questionLimit);
  data.append("timeLimit", timeLimit === null ? "" : ""+timeLimit);
  data.append("timeLeft", timeLeft === null ? "" : ""+timeLeft);
  data.append("conditions", conditions === null ? "" : conditions);
  data.append("report", report === null ? "" : report);
  data.append("overall", overall === null ? "" : ""+overall);
  return axiosBase.post<void>(`/admin/candidates/update/`, data,
    {
      headers: {
        "username": username,
        "candidate-token": token,
        "originalTest": originalTest,
        "skillType": skillType,
      },
    })
};

export const deleteCandidate = (username:string, token:string, skillType:string, testName:string) => {
  return axiosBase.delete<void>(`/admin/candidates/${username}/${token}/`,
                             {
                               headers: {
                                 "skillType": skillType,
                                 "testName": testName,
                               },
                             })
};

export const requestCandidateCertificate = (username: string, readingToken: string,
                                            listeningToken: string, speechUrl: string) => {
  return axiosBase.get<Blob>(`/admin/candidates/certificate`, {
    validateStatus: isStatus2xxOr404,
    responseType: 'blob',
    headers: {
      username: username,
      readingToken: readingToken,
      listeningToken: listeningToken,
      speechUrl: speechUrl,
    },
  });
};

export const getUserResults = (usernameE: string | undefined, isPublic: boolean | undefined) => {
  const urlType = isPublic ? `group`: `teacher`;
  return axiosBase.get<CandidateSummaryDto>(`/${urlType}/candidates/candidateResult/${usernameE}`);
}

// =============================================================================
// == Public endpoints =========================================================
// =============================================================================

export const requestMultiMedia = (publicSrc: string, extension: string, sourceDir: string, subdirectory: string) => {
  return axiosBase.get<Blob>(`/public/${sourceDir}${publicSrc}`, {
    validateStatus: isStatus2xxOr404,
    responseType: 'blob',
    headers: {
      "extension": extension,
      "subdirectory":subdirectory,
    },
  });
};

export const postResendEmail = (id: string, email: string) => {
  return axiosBase.post<boolean>(`/licenseRequest/email/${id}`,
    {},
    {
      headers: {
        "email": email,
      },
    });
};
export const getExistingId = (id: string) => {
  return axiosBase.get<ResponseDto>(`/licenseRequest/exists/${id}`);
};

export const getRegisterPeriod = () => {
  return axiosBase.get<boolean>(`/licenseRequest/periodActive`, {
    validateStatus: isStatus2xxOr404,
  });
};
export const getActiveTest = (sSkillType: string, isDemo:boolean) => {
  return axiosBase.get<TestIdResponseDto>(`/users/test/${sSkillType}`, {
    validateStatus: isStatus2xxOr404,
    headers: {
      "isDemo": isDemo
    },
  });
};

export const getCandidateToken = (publicId: string) => {
  return axiosBase.get<string>(`/users/${publicId}`, {
    validateStatus: isStatus2xxOr404,
  });
};

export const getAbility = (publicId: string, candidateToken: string) => {
  return axiosBase.get<string>(`/users/${publicId}/ability`, {
    validateStatus: isStatus2xxOr404,
    headers: {
      "candidate-token": candidateToken
    },
  });
};

export const getSpeechAbility = (publicId: string, candidateToken: string) => {
  return axiosBase.get<string>(`/users/${publicId}/ability`, {
    validateStatus: isStatus2xxOr404,
    headers: {
      "candidate-token": candidateToken
    },
  });
};

export const getTestByPublicId = (publicId: string, candidateToken: string) => {
  return axiosBase.get<TestDto>(`/public/tests/${publicId}`, {
    validateStatus: isStatus2xxOr404,
    headers: {
      "candidate-token": candidateToken,
    },
  });
};

export const submitLicenseRequestForm = (formData: LicenseRequestDto) => {
  const data = new FormData();
  data.append("age", formData.age.toString());
  data.append("country", formData.country);
  data.append("email", formData.email);
  data.append("email2", formData.email2);
  data.append("education", formData.education);
  data.append("id", formData.id);
  data.append("institution", formData.institution);
  data.append("language", formData.language);
  data.append("name", formData.name);
  data.append("phone", formData.phone);
  data.append("phone2", formData.phone2);
  data.append("region", formData.region);
  data.append("sex", formData.sex);
  return axiosBase.post<ResponseDto>("/licenseRequest/submitRequestForm", data);
};


export const submitUserForm = (username: string, name: string, email: string, region: string) => {
  const data = new FormData();
  data.append("username", username);
  data.append("name", name);
  data.append("email", email);
  data.append("region", region);
  return axiosBase.post<boolean>("/users/submitUserForm", data);
};
export const submitUserFormD = (colePriv: string, zona: string, clasesExtra: string,
                                primeraLenguan: string, sexo: string, email: string) => {
  const data = new FormData();
  data.append("colePriv", colePriv);
  data.append("zona", zona);
  data.append("clasesExtra", clasesExtra);
  data.append("sexo", sexo);
  data.append("primeraLenguan", primeraLenguan);
  data.append("email", email);
  return axiosBase.post<boolean>("/users/DsubmitUserForm", data);
};
export const submitUserFormEDI = (aEDI1: string,aEDI1e: string,aEDI2: string[],aEDI3: string,aEDI4: string,aEDI5: string) => {
  const data = new FormData();
  data.append("aEDI1", aEDI1);
  data.append("aEDI1e", aEDI1e);
  data.append("aEDI2", aEDI2.toString());
  data.append("aEDI3", aEDI3);
  data.append("aEDI4", aEDI4);
  data.append("aEDI5", aEDI5);

  return axiosBase.post<boolean>("/users/submitUserFormEDI", data);
};

export const postTestStart = (publicId: string, candidateToken: string) => {
  return axiosBase.post<void>(
    `/public/tests/${publicId}/start`,
    {},
    {
      headers: {
        "candidate-token": candidateToken,
      },
    }
  );
};

export const postAnswer = (
  publicId: string,
  questionId: string,
  optionIds: string[],
  timesPlayed: number,
  timeLeft: number,
  candidateToken: string
) => {
  const data = new FormData();
  optionIds.forEach((optionId) => data.append("optionIds", optionId));
  data.append("timesPlayed", timesPlayed.toString());
  data.append("timeLeft", timeLeft.toString());
  return axiosBase.post<void>(
    `/public/tests/${publicId}/questions/${questionId}/answer`,
    data,
    {
      headers: {
        "candidate-token": candidateToken,
      },
    }
  );
};

export const updateAnswer = (
  publicId: string,
  candidateToken: string,
  questionId: string,
  timesPlayed: number,
  timeLeft: number
) => {
  const data = new FormData();
  data.append("timesPlayed", timesPlayed.toString());
  data.append("timeLeft", timeLeft.toString());
  return axiosBase.post<void>(
    `/public/tests/${publicId}/questions/${questionId}/updateAnswer`,
    data,
    {
      headers: {
        "candidate-token": candidateToken,
      },
    }
  );
};

export const updateTimeLeft = (
  publicId: string,
  candidateToken: string,
  timeLeft: number
) => {
  const data = new FormData();
  data.append("timeLeft", timeLeft.toString());
  return axiosBase.post<void>(
    `/public/tests/${publicId}/candidate/updateTimeLeft`,
    data,
    {
      headers: {
        "candidate-token": candidateToken,
      },
    }
  );
};

export const getAutomatedCandidateResult = () => {
  return axiosBase.get<string>(`/users/test/sR`, {
    validateStatus: isStatus2xxOr404,
  });
};
export const getAutomatedTest = (skillType: SkillType, isDemo:boolean) => {
  return axiosBase.get<string>(`/users/automatedTest/${skillType}`, {
    validateStatus: isStatus2xxOr404,
    headers: {
      "isDemo": isDemo
    },
  });
};

export const getAutomatedTestExists = (skillType: SkillType, isDemo:boolean) => {
  return axiosBase.get<boolean>(`/users/automatedTest/exists/${skillType}`, {
    validateStatus: isStatus2xxOr404,
    headers: {
      "isDemo": isDemo
    },
  });
};

export const getLicenses = (params: LicenseFilterParams) => {
  const baseUrl = params.isPublic ? "public" : "teacher";
  return axiosBase.get<CandidateSummaryDto[]>(`/${baseUrl}/candidates/licenses`, {
    headers: {
      startDate: params.startDateValue,
      endDate: params.endDateValue,
      license: params.licenseValue,
      username: params.usernameValue,
      name: params.nameValue,
      institution: params.institution,
      region: params.region,
      includeAvailable: params.includeAvailable,
      includeUnavailable: params.includeUnavailable,
      includeInvited: params.includeInvited,
      includeInProgress: params.includeInProgress,
      includeCompleted: params.includeCompleted
    },
  });
}

export const requestLicensesResults = (params: LicenseFilterParams) => {
  return axiosBase.get<Blob>(`/teacher/candidates/resultsL`, {
    validateStatus: isStatus2xxOr404,
    responseType: 'blob',
    headers: {
      startDate: params.startDateValue,
      endDate: params.endDateValue,
      institution: params.institution,
      license: params.licenseValue,
      username: params.usernameValue,
      name: params.nameValue,
      region: params.region,
      includeAvailable: params.includeAvailable,
      includeUnavailable: params.includeUnavailable,
      includeInvited: params.includeInvited,
      includeInProgress: params.includeInProgress,
      includeCompleted: params.includeCompleted
    },
  });
};
export const requestLicenseCertificate = (username: string) => {
  return axiosBase.get<Blob>(`/teacher/candidates/certificate`, {
    validateStatus: isStatus2xxOr404,
    responseType: 'blob',
    headers: {
      username: username
    },
  });
};


export const postCandidateSetActive = (username: string, publicId: string, candidateToken: string,
                                       active: boolean,type:string, isAutomated: boolean) => {
  return axiosBase.post<void>(
    `/group/candidates/setActive`,
    {},
    {
      headers: {
        "username": username,
        "candidate-token": candidateToken,
        "publicId": publicId,
        "active": active,
        "isAutomated": isAutomated
      },
    }
  );
};

export const postCandidatesSetActive = (username: string,
                                       tokenR: string,
                                       testR: string,
                                       tokenL: string,
                                       testL: string,
                                       tokenS: string,
                                       tokenW: string,
                                       active: boolean) => {
  return axiosBase.post<void>(
    `/group/candidates/setActiveAll`,
    {},
    {
      headers: {
        "username": username,
        "tokenR": tokenR !=null ? tokenR:'',
        "testR": testR !=null ? testR:'',
        "tokenL": tokenL !=null ? tokenL:'',
        "testL": testL !=null ? testL:'',
        "tokenS": tokenS !=null ? tokenS:'',
        "tokenW": tokenW !=null ? tokenW:'',
        "active": active
      },
    }
  );
};

export const decodePassword = (password: string) => {
  return axiosBase.get<string>(
    `/public/candidates/decodePassword`,
    {
      headers: {
        "encodedPassword": password
      },
    }
  );
};